import { useEffect, useRef, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { RiStore2Line } from "react-icons/ri";
import BranchNameInput from "./BranchNameInput";
import WorkingDayHoursInputs from "./WorkingDayHoursInputs";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Branch, City } from "@/types/globalTypes";
import { GoogleMapsRegex } from "@/utils/Regexes";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import ToggleSwitch from "../ToggleSwitch";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DeliverySettings from "./DeliverySettings";
import { RootState } from "@/store/store";
import { navigateToSubscribtion } from "@/utils/helpers";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";
import { useUserRole } from "@/hooks/useUserRole";

export type BranchForm = Omit<Branch, "_id" | "userId" | "isMain" | "open" | "availability">;

function AddNewBranch({
  setNeedRefetch,
  allCities,
  branchesLimit,
  branchesLength,
}: {
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  allCities: City[];
  branchesLimit: number;
  branchesLength: number;
}) {
  const {isSuperAdmin,openAppReviewBasedOnRole} = useUserRole()
  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  useLockBodyScroll(isModalOpen);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const BASE_URL = import.meta.env.VITE_BASE_URL;

  const [IsThatMainBranch, setIsThatMainBranch] = useState(
    branchesLength === 0 ? true : false,
  );
  const defaultCenter = { lat: 24.774265, lng: 46.738586 };
  const [enabled, setEnabled] = useState(false);
  const [radius, setRadius] = useState(2);
  const [price, setPrice] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(defaultCenter);
  const [selectedPolygon, setSelectedPolygon] = useState<google.maps.LatLngLiteral[] | null>(null);
  const [mapLocationUrl, setMapLocationUrl] = useState<string | null>(null);
  const apps = useSelector((state: RootState) => state.apps.AppsData);
  const delivery = apps?.find((app) => app.name === "delivery-service");


  useEffect(() => {


    if (mapLocationUrl) {
      formik.setFieldValue("locationUrl", mapLocationUrl);
    } else {
      setMapLocationUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLocationUrl]);
  const handleSetIsThatMainBranch = () => {
    if (branchesLength === 0) {
      toast.error(
        t(
          "addNewBranch.branchRequired"
        ) as string,
      );
      return;
    }
    setIsThatMainBranch(!IsThatMainBranch);
  };

  const TimingOptions = [
    // Below 1 hour
    { value: 5, label: t("addNewBranch.durations.5") },
    { value: 10, label: t("addNewBranch.durations.10") },
    { value: 20, label: t("addNewBranch.durations.20") },
    { value: 30, label: t("addNewBranch.durations.30") },
    { value: 40, label: t("addNewBranch.durations.40") },
    { value: 50, label: t("addNewBranch.durations.50") },
    { value: 60, label: t("addNewBranch.durations.60") },

    // Above 1 hour
    { value: 75, label: t("addNewBranch.durations.75") },
    { value: 90, label: t("addNewBranch.durations.90") },
    { value: 105, label: t("addNewBranch.durations.105") },
    { value: 120, label: t("addNewBranch.durations.120") },
    { value: 135, label: t("addNewBranch.durations.135") },
    { value: 150, label: t("addNewBranch.durations.150") },
    { value: 165, label: t("addNewBranch.durations.165") },
    { value: 180, label: t("addNewBranch.durations.180") }
  ];

  // Formik setup
  const formik = useFormik<BranchForm>({
    initialValues: {
      locationUrl: "",
      phone: "",
      orderPreparationDuration: "",
      nameAR: "",
      nameEN: "",
      address: "",
      country: "Saudi Arabia",
      city: "",
      street: "",
      district: "",
      buildingNumber: "",
      workingDays: [
        {
          day: 0,
          isOpeningDay: false,
          timePeriods: [{ start: "00:00", end: "00:00" }],
        },
        {
          day: 1,
          isOpeningDay: false,
          timePeriods: [{ start: "00:00", end: "00:00" }],
        },
        {
          day: 2,
          isOpeningDay: false,
          timePeriods: [{ start: "00:00", end: "00:00" }],
        },
        {
          day: 3,
          isOpeningDay: false,
          timePeriods: [{ start: "00:00", end: "00:00" }],
        },
        {
          day: 4,
          isOpeningDay: false,
          timePeriods: [{ start: "00:00", end: "00:00" }],
        },
        {
          day: 5,
          isOpeningDay: false,
          timePeriods: [{ start: "00:00", end: "00:00" }],
        },
        {
          day: 6,
          isOpeningDay: false,
          timePeriods: [{ start: "00:00", end: "00:00" }],
        },
      ],
    },
    validationSchema: Yup.object({
      nameAR: Yup.string(),
      nameEN: Yup.string().test(
        "at-least-one-name",
        t("addNewBranch.nameRequired") as string,
        function (value) {
          const { nameAR } = this.parent;
          return Boolean(value?.trim() || nameAR?.trim());
        },
      ),

      locationUrl: Yup.string()
        .url(t("addNewBranch.urlInvalid") as string)
        .required(
          t("addNewBranch.urlRequired") as string,
        )
        .matches(
          GoogleMapsRegex,
          t("addNewBranch.urlValid") as string,
        ),
      phone: Yup.string()
        .required(t("addNewBranch.phoneRequired") as string)
        .matches(
          /^5\d{8}$/,
          t("addNewBranch.phoneValid") as string,
        ),
      orderPreparationDuration: Yup.number()
        .min(0, t("addNewBranch.minValue") as string)
        .required(t("addNewBranch.durationRequired") as string),
      address: Yup.string().required(
        t("addNewBranch.addressRequired") as string,
      ),

    }),
    onSubmit: (values) => {
      const newBranch = {
        nameAR: values.nameAR || "",
        nameEN: values.nameEN || "",
        locationUrl: values.locationUrl || undefined,
        phone: values.phone || undefined,
        orderPreparationDuration: values.orderPreparationDuration || undefined,
        address: values.address || undefined,
        street: values.street || undefined,
        district: values.district || undefined,
        country: values.country || undefined,
        city: values.city || undefined,
        buildingNumber: values.buildingNumber || undefined,
        deliveryEnabled: delivery?.installed && enabled ? true : false,
        deliveryData: enabled && delivery?.installed ? {
          deliveryPrice: +price,
          locationType: selectedPolygon ? "polygon" : "circle",
          locationData: selectedPolygon ? {
            polygon: selectedPolygon || undefined,
            location: {
              lat: selectedLocation.lat,
              lng: selectedLocation.lng,
            }
          } : {
            lat: selectedLocation.lat,
            lng: selectedLocation.lng,
            radius: radius
          },
        } : undefined,
        workingDays:
          values.workingDays && values.workingDays.length > 0
            ? values.workingDays
            : undefined,
        isMain: IsThatMainBranch || undefined,
      };


      const url = `${BASE_URL}/api/dashboard/branches?locale=${lang}`;

      async function createBranch() {
        const loadingToast = toast.loading(
          t("addNewBranch.creating") as string,
        );
        try {
          const response = await axios.post(url, newBranch, {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          });

          if (!response.data.success) {
            toast.error(
              response.data.error ||
              (t("addNewBranch.error") as string),
            );
            return;
          }

          toast.success(
            t("addNewBranch.success") as string,
          );
          setNeedRefetch(true);
          if(isSuperAdmin){
            openAppReviewBasedOnRole()
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            const errorMessage =
              error.response?.data?.error ||
              (t("addNewBranch.error") as string);
            toast.error(errorMessage);
          } else {
            toast.error(
              t("addNewBranch.unexpectedError") as string,
            );
          }
        } finally {
          toast.dismiss(loadingToast);
        }
      }

      createBranch();
    },
  });

  const handleToggle = (day: number) => {
    const updatedDays = formik.values.workingDays.map((dayData) =>
      dayData.day === day
        ? { ...dayData, isOpeningDay: !dayData.isOpeningDay }
        : dayData,
    );
    formik.setFieldValue("workingDays", updatedDays);
  };

  const handleTimeChange = (
    day: number,
    index: number,
    type: "start" | "end",
    value: string,
  ) => {
    const updatedDays = formik.values.workingDays.map((dayData) =>
      dayData.day === day
        ? {
          ...dayData,
          timePeriods: dayData.timePeriods.map((period, idx) =>
            idx === index ? { ...period, [type]: value } : period,
          ),
        }
        : dayData,
    );
    formik.setFieldValue("workingDays", updatedDays);
  };

  const handleBranchNameChange = (newTitle: { ar: string; en: string }) => {
    const updatedValues = {
      ...formik.values,
      nameAR: newTitle.ar,
      nameEN: newTitle.en,
    };

    formik.setValues(updatedValues);
  };

  const handleAddTimePeriod = (index: number) => {
    formik.setFieldValue(
      "workingDays",
      formik.values.workingDays.map((day) =>
        day.day === index
          ? {
            ...day,
            timePeriods: [
              ...day.timePeriods,
              { start: "00:00", end: "00:00" },
            ],
          }
          : day,
      ),
    );
  };

  const handleRemoveTimePeriod = (dayIndex: number, periodIndex: number) => {
    formik.setFieldValue(
      "workingDays",
      formik.values.workingDays.map((day, idx) => {
        if (idx === dayIndex) {
          return {
            ...day,
            timePeriods: day.timePeriods.filter(
              (_, pidx) => pidx !== periodIndex,
            ),
          };
        }
        return day;
      }),
    );
  };


  return (
    <div>
      <button
        onClick={() => setIsModalOpen(true)}
        className="self-end rounded-md bg-sidebar px-4 py-2 text-sm font-medium capitalize text-skyblue md:px-5 md:py-2 md:text-base"
      >
        {t("addNewBranch.title")}
      </button>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px]"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            ref={modalRef}
            className="flex w-[95%] max-w-3xl flex-col gap-7 rounded-lg bg-white px-2 py-3 shadow-lg sm:px-4 sm:py-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm font-semibold sm:text-base md:text-2xl">
                <RiStore2Line className="text-xl" />
                <h2 className="text-lg font-semibold">
                  {t("addNewBranch.title")}
                </h2>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="mb-3 text-gray-600 hover:text-gray-800"
              >
                <IoMdClose size={24} className="text-sm text-red-600" />
              </button>
            </div>

            {branchesLength >= branchesLimit ? (
              <div className=" h-36 w-full items-center py-2 text-sm text-sidebar flex flex-col  justify-between">
                <p className="mb-2 text-center text-xl font-semibold">
                  {t("addNewBranch.maxBranches")}
                </p>
                <button
                  onClick={() => navigateToSubscribtion(navigate, token)}
                  className=" font-semibold text-skyblue bg-sidebar p-2 text-base block rounded-md"
                >
                  {t("branchesSettings.requestBranch")}
                </button>
              </div>
            ) : (
              <form
                onSubmit={formik.handleSubmit}
                id="add-branch-form"
                className="thin-scrollbar max-h-[55dvh] space-y-4 overflow-y-auto px-1"
              >
                {/* Branch name */}
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    {t("addNewBranch.name")}
                  </label>
                  <BranchNameInput
                    BranchNameInputs={{
                      ar: formik.values.nameAR,
                      en: formik.values.nameEN,
                    }}
                    onChange={handleBranchNameChange}
                  />
                  {formik.errors.nameAR && (
                    <div className="text-sm text-red-600">
                      {formik.errors.nameAR}
                    </div>
                  )}
                  {formik.errors.nameEN && (
                    <div className="text-sm text-red-600">
                      {formik.errors.nameEN}
                    </div>
                  )}
                </div>

                {/* Location URL */}
                <div className="flex flex-col">
                  <label
                    htmlFor="locationUrl"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t("addNewBranch.locationUrl")}
                  </label>
                  <input
                    type="url"
                    id="locationUrl"
                    name="locationUrl"
                    value={formik.values.locationUrl}
                    onChange={formik.handleChange}
                    placeholder={
                      t("addNewBranch.enterLocationUrl") as string
                    }
                    className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border"
                  />
                  {formik.errors.locationUrl && (
                    <div className="text-sm text-red-600">
                      {formik.errors.locationUrl}
                    </div>
                  )}
                </div>

                {/* Phone Number */}
                <div className="flex flex-col">
                  <label
                    htmlFor="phone"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t("addNewBranch.phone")}
                  </label>
                  <div dir="ltr" className="relative h-10 w-full">
                    <input
                      type="text"
                      id="phone"
                      name="phone"
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      placeholder={
                        t("addNewBranch.enterPhone") as string
                      }
                      className="h-full w-full rounded-md border border-gray-300 p-2 ps-[65px] focus:outline-none focus:ring-2 focus:ring-border"
                    />
                    <p className="absolute start-0 top-1/2 flex h-full w-[55px] -translate-y-1/2 items-center justify-center rounded-s-md bg-gray-200">
                      <span dir="ltr">+966</span>
                    </p>
                  </div>
                  {formik.errors.phone && (
                    <div className="text-sm text-red-600">
                      {formik.errors.phone}
                    </div>
                  )}
                </div>

                {/* Is Main Branch */}
                <div className="flex flex-wrap items-center justify-between rounded-md border border-gray-300 px-2 py-3">
                  <p>
                    {t("addNewBranch.isMain")}
                  </p>
                  <ToggleSwitch
                    size="small"
                    checked={IsThatMainBranch}
                    onChange={handleSetIsThatMainBranch}
                  />
                </div>

                {/* Country */}
                <div className="flex flex-col">
                  <label
                    htmlFor="country"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t("addNewBranch.country")}
                  </label>
                  <select
                    id="country"
                    name="country"
                    className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border disabled:cursor-not-allowed disabled:bg-gray-300"
                    value={formik.values.country}
                    disabled
                  >
                    <option value="Saudi Arabia">
                      {t("addNewBranch.saudiArabia")}
                    </option>
                  </select>
                </div>

                {/* City */}
                <div className="flex flex-col">
                  <label
                    htmlFor="city"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t("addNewBranch.city")}
                  </label>
                  <select
                    id="city"
                    name="city"
                    className={`mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border disabled:cursor-not-allowed disabled:bg-gray-300 ${formik.values.city === "" ? "text-gray-500" : ""}`}
                    value={formik.values.city}
                    onChange={formik.handleChange}
                  >
                    <option value="" disabled>
                      Select a city
                    </option>
                    {allCities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {lang === "ar" ? city.nameAR : city.nameEN}
                      </option>
                    ))}
                  </select>
                  {formik.errors.city && (
                    <div className="text-sm text-red-600">
                      {formik.errors.city}
                    </div>
                  )}
                </div>

                {/* Grid inputs */}
                <div className="gap-x-3 gap-y-4 space-y-3 md:grid md:grid-cols-2 md:grid-rows-2 md:space-y-0">
                  {/* Address */}
                  <div className="col-start-1 col-end-2 row-start-1 row-end-1 flex flex-col">
                    <label
                      htmlFor="address"
                      className="text-sm font-medium text-gray-700"
                    >
                      {t("addNewBranch.address")}
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      placeholder={
                        t("addNewBranch.enterAddress") as string
                      }
                      className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border"
                    />
                    {formik.errors.address && (
                      <div className="text-sm text-red-600">
                        {formik.errors.address}
                      </div>
                    )}
                  </div>

                  {/* Street */}
                  <div className="col-start-2 col-end-3 row-start-1 row-end-1 flex flex-col">
                    <label
                      htmlFor="street"
                      className="text-sm font-medium text-gray-700"
                    >
                      {t("addNewBranch.street")}
                    </label>
                    <input
                      type="text"
                      id="street"
                      name="street"
                      value={formik.values.street}
                      onChange={formik.handleChange}
                      placeholder={
                        t("addNewBranch.enterStreet") as string
                      }
                      className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border"
                    />
                    {formik.errors.street && (
                      <div className="text-sm text-red-600">
                        {formik.errors.street}
                      </div>
                    )}
                  </div>

                  {/* District */}
                  <div className="col-start-1 col-end-2 row-start-2 row-end-2 flex flex-col">
                    <label
                      htmlFor="district"
                      className="text-sm font-medium text-gray-700"
                    >
                      {t("addNewBranch.district")}
                    </label>
                    <input
                      type="text"
                      id="district"
                      name="district"
                      value={formik.values.district}
                      onChange={formik.handleChange}
                      placeholder={
                        t("addNewBranch.enterDistrict") as string
                      }
                      className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border"
                    />
                    {formik.errors.district && (
                      <div className="text-sm text-red-600">
                        {formik.errors.district}
                      </div>
                    )}
                  </div>

                  {/* Building Number */}
                  <div className="col-start-2 col-end-3 row-start-2 row-end-2 flex flex-col">
                    <label
                      htmlFor="buildingNumber"
                      className="text-sm font-medium text-gray-700"
                    >
                      {t("addNewBranch.buildingNumber")}
                    </label>
                    <input
                      type="text"
                      id="buildingNumber"
                      name="buildingNumber"
                      value={formik.values.buildingNumber}
                      onChange={formik.handleChange}
                      placeholder={
                        t("addNewBranch.enterBuildingNumber") as string
                      }
                      className="mt-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border"
                    />
                    {formik.errors.buildingNumber && (
                      <div className="text-sm text-red-600">
                        {formik.errors.buildingNumber}
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Preparation Duration */}
                <div className="flex flex-col">
                  <label
                    htmlFor="orderPreparationDuration"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t("addNewBranch.prepDuration")}
                  </label>

                  <div className="relative h-12 w-full">
                    {/* Input field */}
                    <input
                      type="number"
                      min={0}
                      id="orderPreparationDuration"
                      name="orderPreparationDuration"
                      value={formik.values.orderPreparationDuration}
                      onChange={formik.handleChange}
                      placeholder={
                        t("addNewBranch.enterPrepDuration") as string
                      }
                      className={`mt-1 w-full rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border ${i18n.dir() === "rtl" ? "pe-[120px]" : "pe-[140px]"}`}
                    />

                    {/* Select field */}
                    <select
                      className={`thin-scrollbar absolute end-1 top-1/2 h-[39px] -translate-y-1/2 rounded-e-md border-s-2 border-gray-200 ps-1 ${i18n.language === "ar" ? "w-[100px]" : "w-[125px]"}`}
                      name="orderPreparationDurationSelect"
                      id="orderPreparationDurationSelect"
                      value={
                        TimingOptions.some(
                          (option) =>
                            option.value ===
                            Number(formik.values.orderPreparationDuration),
                        )
                          ? formik.values.orderPreparationDuration
                          : "none"
                      }
                      onChange={(e) => {
                        formik.setFieldValue(
                          "orderPreparationDuration",
                          e.target.value !== "none"
                            ? Number(e.target.value)
                            : "",
                        );
                      }}
                    >
                      {/* Placeholder option */}
                      <option value="none" disabled>
                        {t("addNewBranch.chooseTime")}
                      </option>
                      {TimingOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formik.errors.orderPreparationDuration && (
                    <div className="text-sm text-red-600">
                      {formik.errors.orderPreparationDuration}
                    </div>
                  )}
                </div>
                {delivery?.installed && (
                  <DeliverySettings
                    enabled={enabled}
                    setEnabled={setEnabled}
                    radius={radius}
                    setRadius={setRadius}
                    price={price}
                    setPrice={setPrice}
                    selectedLocation={selectedLocation}
                    setSelectedLocation={setSelectedLocation}
                    selectedPolygon={selectedPolygon}
                    setSelectedPolygon={setSelectedPolygon}
                    locationUrl={formik.values.locationUrl}
                    setLocationUrl={setMapLocationUrl}
                  />
                )}
                {/* Working Day Hours */}
                <div className="flex flex-col">
                  <label
                    htmlFor="workingDayHours"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t("branchCard.workingHours")}
                  </label>

                  <WorkingDayHoursInputs
                    handleAddTimePeriod={handleAddTimePeriod}
                    handleRemoveTimePeriod={handleRemoveTimePeriod}
                    handleTimeChange={handleTimeChange}
                    handleToggle={handleToggle}
                    workingDays={formik.values.workingDays}
                  />
                </div>
              </form>
            )}

            {branchesLength >= branchesLimit ? null : (
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  form="add-branch-form"
                  className="w-fit rounded-md bg-sidebar px-4 py-2 text-sm font-semibold text-skyblue md:text-base"
                >
                  {t("addNewBranch.title")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AddNewBranch;
